import MetadataLanguageField, {metadataText} from '@/components/MetadataLanguageField';
import React, { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { useLang } from "@/i18n/LanguageContext";
import { Star, X, ArrowUp, ArrowDown, Upload } from "lucide-react";

const MAX = 10;
const MAX_BYTES = 10 * 1024 * 1024;
const TYPES = ["image/jpeg", "image/png", "image/webp"];

export default function GalleryEditor({ farmId, originalLanguage = 'it' }) {
  const { lang } = useLang(); const it = lang === "it";
  const [items, setItems] = useState([]);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const fileRef = useRef(null);

  useEffect(() => {
    let on = true;
    api.get(`/farms/${farmId}`).then(r => {
      if (!on) return;
      const g = (r.data.gallery || []).map((x, i) => typeof x === "string" ? { id: `legacy-${i}`, url: x, caption: "", cover: i === 0 } : x);
      setItems(g);
    }).catch(() => {});
    return () => { on = false; };
  }, [farmId]);

  const persist = async (next) => {
    if (busy) return;
    const previous = items; setBusy(true); setErr(''); setItems(next);
    try { const r = await api.put(`/farms/${farmId}/gallery`, { gallery: next }); setItems(r.data.gallery); }
    catch (e) { setItems(previous); setErr(e.response?.data?.detail || "Error"); }
    finally { setBusy(false); }
  };

  const onFiles = async (files) => {
    setErr("");
    let current = items;
    for (const file of Array.from(files)) {
      if (current.length >= MAX) { setErr(it ? `Massimo ${MAX} immagini: rimuovine una per aggiungerne altre.` : `Maximum ${MAX} images: remove one to add more.`); break; }
      if (!TYPES.includes(file.type)) { setErr(it ? "Formati ammessi: JPEG, PNG, WebP." : "Allowed formats: JPEG, PNG, WebP."); continue; }
      if (file.size > MAX_BYTES) { setErr(it ? "Ogni file può pesare al massimo 10 MB." : "Each file can be at most 10 MB."); continue; }
      const dataUrl = await new Promise((res) => { const fr = new FileReader(); fr.onload = () => res(fr.result); fr.readAsDataURL(file); });
      setBusy(true);
      try {
        const r = await api.post(`/farms/${farmId}/gallery`, { image: dataUrl, caption: "" });
        current = r.data.gallery; setItems(current);
      } catch (e) { setErr(e.response?.data?.detail || "Upload error"); setBusy(false); break; }
      setBusy(false);
    }
    if (fileRef.current) fileRef.current.value = "";
  };

  const setCover = (id) => persist(items.map(g => ({ ...g, cover: g.id === id })));
  const remove = (id) => persist(items.filter(g => g.id !== id));
  const setCaption = (id, patch) => setItems(items.map(g => g.id === id ? { ...g, ...patch } : g));
  const move = (i, d) => { const n = [...items]; const j = i + d; if (j < 0 || j >= n.length) return; [n[i], n[j]] = [n[j], n[i]]; persist(n); };

  return (
    <section className="space-y-3" data-testid="gallery-editor">
      <h3 className="font-serif text-xl">{it ? "Galleria fotografica" : "Photo gallery"}</h3>
      <p className="text-sm text-[#5C4636]">{it ? `Fino a ${MAX} immagini (inclusa la copertina), uguale per Free e Pro. Max 10 MB per file; JPEG, PNG o WebP. Le immagini vengono ottimizzate e i metadati di posizione (GPS) rimossi.` : `Up to ${MAX} images (cover included), the same for Free and Pro. Max 10 MB per file; JPEG, PNG or WebP. Images are optimized and location (GPS) metadata is removed.`}</p>
      <p className="text-sm" role="status">{busy ? (it ? "Salvataggio in corso…" : "Saving…") : (it ? "Le immagini vengono salvate automaticamente al caricamento; le didascalie quando esci dal campo." : "Images are saved automatically on upload; captions are saved when you leave the field.")}</p>
      <div className="flex items-center gap-3">
        <input ref={fileRef} data-testid="gallery-input" type="file" accept="image/jpeg,image/png,image/webp" multiple hidden onChange={e => onFiles(e.target.files)} />
        <button type="button" disabled={busy || items.length >= MAX} onClick={() => fileRef.current?.click()}
          className="inline-flex items-center gap-2 rounded-full bg-[#557448] text-white px-4 py-2 text-sm disabled:opacity-50">
          <Upload size={16} /> {busy ? (it ? "Carico…" : "Uploading…") : (it ? "Aggiungi immagini" : "Add images")}
        </button>
        <span className="text-sm text-[#6E6258]">{items.length}/{MAX}</span>
      </div>
      {err && <p className="text-sm text-[#B3261E]" role="alert">{err}</p>}
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
        {items.map((g, i) => (
          <div key={g.id} className="border border-[#E2DACB] rounded-md p-2 space-y-2">
            <div className="relative">
              <img src={g.url} alt={metadataText(g,"caption",lang)} loading="lazy" className="rounded object-cover h-28 w-full" />
              {g.cover && <span className="absolute top-1 left-1 bg-[#E3B24C] text-[#2A2521] text-xs px-1.5 py-0.5 rounded">{it ? "Copertina" : "Cover"}</span>}
            </div>
            <MetadataLanguageField row={g} field="caption" originalLanguage={originalLanguage} label={it ? "Didascalia" : "Caption"} multiline={false} maxLength={300} disabled={busy} onChange={patch=>setCaption(g.id,patch)} onBlur={()=>persist(items)} />
            <div className="flex items-center justify-between text-[#557448]">
              <button type="button" title={it ? "Imposta copertina" : "Set cover"} onClick={() => setCover(g.id)}><Star size={15} className={g.cover ? "fill-[#E3B24C] text-[#E3B24C]" : ""} /></button>
              <button type="button" title={it ? "Sposta su" : "Move up"} onClick={() => move(i, -1)}><ArrowUp size={15} /></button>
              <button type="button" title={it ? "Sposta giù" : "Move down"} onClick={() => move(i, 1)}><ArrowDown size={15} /></button>
              <button type="button" title={it ? "Rimuovi" : "Remove"} data-testid={`gallery-remove-${i}`} onClick={() => remove(g.id)} className="text-[#B3261E]"><X size={15} /></button>
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}

